import React from "react";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { OnboardingState } from "../../../../types/onboarding";
import {
  MORE_INFO_NEEDED,
  PIPELINE_STATES,
  RESIGNED,
  stateLabel,
} from "../../../../utils/onboardingStates";
import {
  LEAD_SOURCE_OPTIONS,
  SIGN_METHOD_OPTIONS,
} from "../../../../constants/contractSigning";
import { SETUP_FEE_STATUS_OPTIONS } from "../../../../utils/setupFee";

export interface ProspectFilters {
  states: string[];
  customerType: string | null;
  signMethod: string | null;
  leadSource: string | null;
  vendor: string | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  dateField: "created" | "last_changed";
  setupFeeStatus: string | null;
}

export const EMPTY_FILTERS: ProspectFilters = {
  states: [],
  customerType: null,
  signMethod: null,
  leadSource: null,
  vendor: null,
  dateFrom: null,
  dateTo: null,
  dateField: "created",
  setupFeeStatus: null,
};

// Every state a prospect can be in, including the two that sit outside the
// ordered pipeline — an agent filtering for resigned prospects is a real case.
const STATE_OPTIONS = [
  ...PIPELINE_STATES,
  MORE_INFO_NEEDED,
  RESIGNED,
].map((s) => ({ value: s, label: stateLabel(s as OnboardingState) }));

const CUSTOMER_TYPE_OPTIONS = [
  { value: "PRIVATE", label: "Private" },
  { value: "BUSINESS", label: "Business" },
];

const DATE_FIELD_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "last_changed", label: "Last changed" },
];

/**
 * True when the range is back to front.
 *
 * Checked here rather than left to the API: its refusal reads
 * "date_from must not be after date_to", which is a wire contract talking to
 * itself, not something an agent should be shown (FR-017).
 */
export const isRangeInverted = (filters: ProspectFilters): boolean =>
  !!filters.dateFrom &&
  !!filters.dateTo &&
  filters.dateFrom.getTime() > filters.dateTo.getTime();

interface ProspectTableFiltersProps {
  filters: ProspectFilters;
  onChange: (filters: ProspectFilters) => void;
  vendorOptions: string[];
}

const ProspectTableFilters: React.FC<ProspectTableFiltersProps> = ({
  filters,
  onChange,
  vendorOptions,
}) => {
  const set = <K extends keyof ProspectFilters>(
    key: K,
    value: ProspectFilters[K]
  ) => onChange({ ...filters, [key]: value });

  const anyFilter =
    filters.states.length > 0 ||
    filters.customerType !== null ||
    filters.signMethod !== null ||
    filters.leadSource !== null ||
    filters.vendor !== null ||
    filters.setupFeeStatus !== null ||
    filters.dateFrom !== null ||
    filters.dateTo !== null;

  return (
    <div className="flex flex-wrap gap-2 align-items-end mb-3">
      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Stage</label>
        <MultiSelect
          value={filters.states}
          options={STATE_OPTIONS}
          onChange={(e) => set("states", e.value)}
          placeholder="Any stage"
          display="chip"
          filter
          className="w-16rem"
          aria-label="Filter by stage"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Customer type</label>
        <Dropdown
          value={filters.customerType}
          options={CUSTOMER_TYPE_OPTIONS}
          onChange={(e) => set("customerType", e.value)}
          placeholder="Any"
          showClear
          className="w-10rem"
          aria-label="Filter by customer type"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Sign method</label>
        <Dropdown
          value={filters.signMethod}
          options={SIGN_METHOD_OPTIONS}
          onChange={(e) => set("signMethod", e.value)}
          placeholder="Any"
          showClear
          className="w-12rem"
          aria-label="Filter by sign method"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Lead source</label>
        <Dropdown
          value={filters.leadSource}
          options={LEAD_SOURCE_OPTIONS}
          onChange={(e) => set("leadSource", e.value)}
          placeholder="Any"
          showClear
          filter
          className="w-12rem"
          aria-label="Filter by lead source"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Setup fee</label>
        <Dropdown
          value={filters.setupFeeStatus}
          options={SETUP_FEE_STATUS_OPTIONS}
          onChange={(e) => set("setupFeeStatus", e.value)}
          placeholder="Any"
          showClear
          className="w-10rem"
          aria-label="Filter by setup fee"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Vendor</label>
        {vendorOptions.length ? (
          <Dropdown
            value={filters.vendor}
            options={vendorOptions.map((v) => ({ value: v, label: v }))}
            onChange={(e) => set("vendor", e.value)}
            placeholder="Any"
            showClear
            filter
            className="w-12rem"
            aria-label="Filter by vendor"
          />
        ) : (
          <InputText
            value={filters.vendor ?? ""}
            onChange={(e) => set("vendor", e.target.value || null)}
            placeholder="Any"
            className="w-12rem"
            aria-label="Filter by vendor"
          />
        )}
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">Date range applies to</label>
        <SelectButton
          value={filters.dateField}
          options={DATE_FIELD_OPTIONS}
          onChange={(e) => e.value && set("dateField", e.value)}
          allowEmpty={false}
          aria-label="Date field"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">From</label>
        <Calendar
          value={filters.dateFrom}
          onChange={(e) => set("dateFrom", (e.value as Date) ?? null)}
          dateFormat="dd.mm.yy"
          showButtonBar
          className="w-10rem"
          aria-label="From date"
        />
      </div>

      <div className="flex flex-column gap-1">
        <label className="text-sm text-500">To</label>
        <Calendar
          value={filters.dateTo}
          onChange={(e) => set("dateTo", (e.value as Date) ?? null)}
          dateFormat="dd.mm.yy"
          showButtonBar
          className="w-10rem"
          aria-label="To date"
        />
      </div>

      {anyFilter && (
        <Button
          label="Clear"
          icon="pi pi-filter-slash"
          outlined
          size="small"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
        />
      )}
    </div>
  );
};

export default ProspectTableFilters;
